import React from 'react';
import { FileText, Pencil, X, Save, Loader2 } from 'lucide-react';
import type { Subtopico } from './types';

interface EcadMetadataSectionProps {
  selectedRoteiro: Subtopico | null;
  roteiroEditForm: {
    titulo: string;
    descricao: string;
    videoUrl: string;
    pdfUrl: string;
    duracao: string;
    dificuldade: string;
    urlInicial: string;
    velocidadeDigitacaoMs: number;
    mouseSteps: number;
    vozTutor: string;
  };
  setRoteiroEditForm: React.Dispatch<React.SetStateAction<{
    titulo: string;
    descricao: string;
    videoUrl: string;
    pdfUrl: string;
    duracao: string;
    dificuldade: string;
    urlInicial: string;
    velocidadeDigitacaoMs: number;
    mouseSteps: number;
    vozTutor: string;
  }>>;
  isEditingMetadata: boolean;
  setIsEditingMetadata: (val: boolean) => void;
  savingRoteiroMeta: boolean;
  handleSaveRoteiroMeta: () => Promise<void>;
  handleCancelEditMeta: () => void;
}

export const EcadMetadataSection: React.FC<EcadMetadataSectionProps> = ({
  selectedRoteiro,
  roteiroEditForm,
  setRoteiroEditForm,
  isEditingMetadata,
  setIsEditingMetadata,
  savingRoteiroMeta,
  handleSaveRoteiroMeta,
  handleCancelEditMeta
}) => {
  return (
    <section style={{
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#7c3aed', fontFamily: "'Outfit', sans-serif", margin: 0 }}>
          Metadados do Roteiro (Capacitação)
        </h3>
        
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {roteiroEditForm.pdfUrl && (
            <a
              href={roteiroEditForm.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '8px',
                padding: '6px 12px',
                fontSize: '0.75rem',
                color: '#ef4444',
                fontWeight: 600,
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              <FileText size={12} />
              Manual PDF
            </a>
          )}

          {!isEditingMetadata ? (
            <button
              onClick={() => setIsEditingMetadata(true)}
              style={{
                background: '#f1f5f9',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '6px 12px',
                fontSize: '0.75rem',
                color: '#475569',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
            >
              <Pencil size={12} />
              Editar
            </button>
          ) : (
            <>
              <button
                onClick={handleCancelEditMeta}
                style={{
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  fontSize: '0.75rem',
                  color: '#ef4444',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <X size={12} />
                Cancelar
              </button>
              <button
                onClick={handleSaveRoteiroMeta}
                disabled={savingRoteiroMeta}
                style={{
                  background: 'rgba(16, 185, 129, 0.08)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  fontSize: '0.75rem',
                  color: '#059669',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {savingRoteiroMeta ? <Loader2 size={12} className="spin" /> : <Save size={12} />}
                Salvar Metadados
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{
        display: (selectedRoteiro?.videoUrl && !isEditingMetadata) ? 'grid' : 'block',
        gridTemplateColumns: (selectedRoteiro?.videoUrl && !isEditingMetadata) ? '1fr 340px' : 'none',
        gap: '20px',
        alignItems: 'start'
      }}>
        <div>
          {!isEditingMetadata ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.92rem', color: '#1e293b', fontWeight: 800 }}>
                  {roteiroEditForm.titulo || '(Sem Título)'}
                </span>
                {roteiroEditForm.duracao && (
                  <span style={{ 
                    fontSize: '0.72rem', 
                    background: '#f1f5f9', 
                    border: '1px solid #e2e8f0', 
                    color: '#475569', 
                    padding: '2px 8px', 
                    borderRadius: '12px',
                    fontWeight: 600
                  }}>
                    ⏱️ {roteiroEditForm.duracao}
                  </span>
                )}
                {roteiroEditForm.dificuldade && (
                  <span style={{ 
                    fontSize: '0.72rem', 
                    background: roteiroEditForm.dificuldade === 'Avançado' ? 'rgba(239,68,68,0.08)' : roteiroEditForm.dificuldade === 'Intermediário' ? 'rgba(217,119,6,0.08)' : 'rgba(16,185,129,0.08)', 
                    border: '1px solid',
                    borderColor: roteiroEditForm.dificuldade === 'Avançado' ? 'rgba(239,68,68,0.2)' : roteiroEditForm.dificuldade === 'Intermediário' ? 'rgba(217,119,6,0.2)' : 'rgba(16,185,129,0.2)', 
                    color: roteiroEditForm.dificuldade === 'Avançado' ? '#ef4444' : roteiroEditForm.dificuldade === 'Intermediário' ? '#d97706' : '#10b981', 
                    padding: '2px 8px', 
                    borderRadius: '12px',
                    fontWeight: 600
                  }}>
                    ⚡ {roteiroEditForm.dificuldade}
                  </span>
                )}
              </div>
              <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0, lineHeight: '1.5' }}>
                {roteiroEditForm.descricao || '(Sem descrição cadastrada)'}
              </p>
              <div style={{ 
                marginTop: '12px', 
                display: 'flex', 
                gap: '12px', 
                flexWrap: 'wrap',
                paddingTop: '12px',
                borderTop: '1px solid #e2e8f0'
              }}>
                <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                  🌐 URL Inicial: <strong style={{ color: '#1e293b' }}>{roteiroEditForm.urlInicial || 'http://localhost:3443'}</strong>
                </span>
                <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                  ⌨️ Digitação: <strong style={{ color: '#1e293b' }}>{roteiroEditForm.velocidadeDigitacaoMs || 60}ms</strong>
                </span>
                <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                  🖱️ Passos Mouse: <strong style={{ color: '#1e293b' }}>{roteiroEditForm.mouseSteps || 45}</strong>
                </span>
                <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                  🗣️ Voz Tutor: <strong style={{ color: '#1e293b' }}>{roteiroEditForm.vozTutor || 'Kore'}</strong>
                </span>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>Título do Roteiro</label>
                  <input 
                    type="text" 
                    value={roteiroEditForm.titulo}
                    onChange={(e) => setRoteiroEditForm(prev => ({ ...prev, titulo: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: '#ffffff',
                      color: '#1e293b',
                      fontSize: '0.85rem',
                      boxSizing: 'border-box',
                      fontWeight: 500
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>Duração Estimada</label>
                  <input 
                    type="text" 
                    value={roteiroEditForm.duracao}
                    onChange={(e) => setRoteiroEditForm(prev => ({ ...prev, duracao: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: '#ffffff',
                      color: '#1e293b',
                      fontSize: '0.85rem',
                      boxSizing: 'border-box',
                      fontWeight: 500
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>Dificuldade</label>
                  <select
                    value={roteiroEditForm.dificuldade}
                    onChange={(e) => setRoteiroEditForm(prev => ({ ...prev, dificuldade: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: '#ffffff',
                      color: '#1e293b',
                      fontSize: '0.85rem',
                      boxSizing: 'border-box',
                      outline: 'none',
                      fontWeight: 500
                    }}
                  >
                    <option value="Básico">Básico</option>
                    <option value="Médio">Médio</option>
                    <option value="Avançado">Avançado</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>URL do Vídeo</label>
                  <input 
                    type="text" 
                    value={roteiroEditForm.videoUrl}
                    onChange={(e) => setRoteiroEditForm(prev => ({ ...prev, videoUrl: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: '#ffffff',
                      color: '#1e293b',
                      fontSize: '0.85rem',
                      boxSizing: 'border-box',
                      fontWeight: 500
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>URL do PDF Manual</label>
                  <input 
                    type="text" 
                    value={roteiroEditForm.pdfUrl}
                    onChange={(e) => setRoteiroEditForm(prev => ({ ...prev, pdfUrl: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: '#ffffff',
                      color: '#1e293b',
                      fontSize: '0.85rem',
                      boxSizing: 'border-box',
                      fontWeight: 500
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>Descrição / Objetivo Pedagógico</label>
                <textarea 
                  value={roteiroEditForm.descricao}
                  onChange={(e) => setRoteiroEditForm(prev => ({ ...prev, descricao: e.target.value }))}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    color: '#1e293b',
                    fontSize: '0.85rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    fontWeight: 500
                  }}
                />
              </div>

              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid #e2e8f0',
                marginBottom: '14px'
              }}>
                <h4 style={{ fontSize: '0.82rem', fontWeight: 700, color: '#7c3aed', marginBottom: '10px', marginTop: 0 }}>
                  Configurações do Simulador Playwright
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>URL Inicial do Sistema</label>
                    <input 
                      type="text" 
                      value={roteiroEditForm.urlInicial || ''}
                      onChange={(e) => setRoteiroEditForm(prev => ({ ...prev, urlInicial: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        background: '#ffffff',
                        color: '#1e293b',
                        fontSize: '0.85rem',
                        boxSizing: 'border-box',
                        fontWeight: 500
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>Velocidade Digitação (ms)</label>
                    <input 
                      type="number" 
                      value={roteiroEditForm.velocidadeDigitacaoMs || 60}
                      onChange={(e) => setRoteiroEditForm(prev => ({ ...prev, velocidadeDigitacaoMs: Number(e.target.value) }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        background: '#ffffff',
                        color: '#1e293b',
                        fontSize: '0.85rem',
                        boxSizing: 'border-box',
                        fontWeight: 500
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>Passos do Mouse</label>
                    <input 
                      type="number" 
                      value={roteiroEditForm.mouseSteps || 45}
                      onChange={(e) => setRoteiroEditForm(prev => ({ ...prev, mouseSteps: Number(e.target.value) }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        background: '#ffffff',
                        color: '#1e293b',
                        fontSize: '0.85rem',
                        boxSizing: 'border-box',
                        fontWeight: 500
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 700, marginBottom: '6px' }}>Voz do Tutor (Gemini)</label>
                    <select
                      value={roteiroEditForm.vozTutor || 'Kore'}
                      onChange={(e) => setRoteiroEditForm(prev => ({ ...prev, vozTutor: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        background: '#ffffff',
                        color: '#1e293b',
                        fontSize: '0.85rem',
                        boxSizing: 'border-box',
                        fontWeight: 500
                      }}
                    >
                      <option value="Kore">Kore (Feminino Padrão)</option>
                      <option value="Puck">Puck (Masculino)</option>
                      <option value="Charon">Charon (Grave)</option>
                      <option value="Fenrir">Fenrir (Jovem)</option>
                      <option value="Aoede">Aoede (Agudo)</option>
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {(selectedRoteiro?.videoUrl && !isEditingMetadata) && (
          <div style={{
            width: '100%',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            background: '#ffffff',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
          }}>
            <video 
              src={selectedRoteiro.videoUrl} 
              controls 
              style={{ width: '100%', display: 'block', maxHeight: '180px', objectFit: 'contain' }} 
            />
          </div>
        )}
      </div>
    </section>
  );
};
